import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import {
  DepartmentStateTab,
  DepartmentsView,
  DepartmentTreeItem,
} from '../department-model';
import { DepartmentService } from '../department.service';
import {
  buildDepartmentTree,
  buildOrgChartPositions,
  buildPositionForests,
  countMatches,
  expandableDepartmentIds,
  expandableOrgChartIds,
  filterDepartmentTree,
} from './department-tree.utils';
import { DEPARTMENT_TREE_DEFAULT_PAGE_SIZE } from './departments.constant';
import { SearchService } from '../../../../../../ui-components/lib/search-field/search.service';

@Injectable()
export class DepartmentsStore {
  private readonly departmentService = inject(DepartmentService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly searchService = inject(SearchService);

  readonly departmentItems = signal<DepartmentTreeItem[]>([]);
  readonly isDepartmentsLoading = signal(false);
  readonly hasDepartmentsError = signal(false);

  readonly tab = signal<DepartmentStateTab>('all');
  readonly search = signal('');
  readonly view = signal<DepartmentsView>('list');
  readonly listExpandedIds = signal<ReadonlySet<number>>(new Set());
  readonly isListForceExpanded = signal(false);
  readonly chartExpandedIds = signal<ReadonlySet<number>>(new Set());
  readonly page = signal(1);
  readonly pageSize = signal(DEPARTMENT_TREE_DEFAULT_PAGE_SIZE);
  readonly selectedDepartmentId = signal<number | null>(null);

  readonly tree = computed(() => buildDepartmentTree(this.departmentItems()));
  readonly appliedSearch = computed(() =>
    this.searchService.checkSearchLength(this.search().trim())
  );
  readonly visibleTree = computed(() =>
    filterDepartmentTree(this.tree(), {
      tab: this.tab(),
      search: this.appliedSearch(),
    })
  );
  readonly isSearching = computed(() => this.appliedSearch().length > 0);
  readonly departmentsTotal = computed(() => countMatches(this.visibleTree()));
  readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.visibleTree().length / this.pageSize()))
  );
  readonly pagedRoots = computed(() => {
    const start = (this.page() - 1) * this.pageSize();
    return this.visibleTree().slice(start, start + this.pageSize());
  });
  readonly positions = computed(() =>
    this.departmentItems().flatMap(department =>
      buildOrgChartPositions(department)
    )
  );
  readonly positionForests = computed(() =>
    buildPositionForests(this.positions())
  );
  readonly selectedDepartment = computed(
    () =>
      this.departmentItems().find(
        department => department.id === this.selectedDepartmentId()
      ) ?? null
  );
  private readonly visibleExpandableIds = computed(() =>
    expandableDepartmentIds(this.visibleTree())
  );
  readonly canExpandList = computed(() => {
    if (this.isListForceExpanded()) {
      return false;
    }

    const expanded = this.listExpandedIds();
    for (const id of this.visibleExpandableIds()) {
      if (!expanded.has(id)) {
        return true;
      }
    }
    return false;
  });
  readonly canCollapseList = computed(() => {
    if (this.visibleExpandableIds().size === 0) {
      return false;
    }
    if (this.isListForceExpanded()) {
      return true;
    }

    const expanded = this.listExpandedIds();
    for (const id of this.visibleExpandableIds()) {
      if (expanded.has(id)) {
        return true;
      }
    }
    return false;
  });

  load(): void {
    this.loadDepartments();
  }

  loadDepartments(): void {
    this.isDepartmentsLoading.set(true);
    this.hasDepartmentsError.set(false);
    this.departmentService
      .getDepartmentTree()
      .pipe(
        finalize(() => this.isDepartmentsLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: response => {
          const isFirstLoad = this.departmentItems().length === 0;
          this.departmentItems.set(response.data);
          if (isFirstLoad) {
            this.listExpandedIds.set(
              new Set(expandableDepartmentIds(this.tree()))
            );
            this.chartExpandedIds.set(
              new Set(
                expandableOrgChartIds(this.tree(), this.positionForests())
              )
            );
          }
          this.page.update(page => Math.min(page, this.totalPages()));
        },
        error: () => this.hasDepartmentsError.set(true),
      });
  }

  setTab(tab: DepartmentStateTab): void {
    this.tab.set(tab);
    this.page.set(1);
  }

  setSearch(search: string): void {
    const wasSearching = this.appliedSearch().length > 0;
    this.search.set(search);
    this.page.set(1);
    const isSearching = this.appliedSearch().length > 0;
    if (!wasSearching && isSearching) {
      this.isListForceExpanded.set(true);
      this.chartExpandedIds.update(ids => {
        const next = new Set(ids);
        expandableOrgChartIds(
          this.visibleTree(),
          this.positionForests()
        ).forEach(id => next.add(id));
        return next;
      });
    }
    if (!isSearching) {
      this.isListForceExpanded.set(false);
    }
  }

  setPageSize(size: number): void {
    this.pageSize.set(size);
    this.page.set(1);
  }

  toggleListExpanded(id: number): void {
    if (this.isListForceExpanded()) {
      this.isListForceExpanded.set(false);
      const visibleIds = this.visibleExpandableIds();
      this.listExpandedIds.update(ids => {
        const next = new Set(ids);
        visibleIds.forEach(visibleId => next.add(visibleId));
        return toggleId(next, id);
      });
      return;
    }

    this.listExpandedIds.update(ids => toggleId(ids, id));
  }

  expandList(): void {
    this.isListForceExpanded.set(false);
    const visibleIds = this.visibleExpandableIds();
    this.listExpandedIds.update(ids => {
      const next = new Set(ids);
      visibleIds.forEach(id => next.add(id));
      return next;
    });
  }

  collapseList(): void {
    this.isListForceExpanded.set(false);
    const visibleIds = this.visibleExpandableIds();
    this.listExpandedIds.update(ids => {
      const next = new Set(ids);
      visibleIds.forEach(id => next.delete(id));
      return next;
    });
  }

  toggleChartExpanded(id: number): void {
    this.chartExpandedIds.update(ids => toggleId(ids, id));
  }

  selectDepartment(id: number): void {
    this.selectedDepartmentId.set(id);
  }

  closeQuickView(): void {
    this.selectedDepartmentId.set(null);
  }
}

function toggleId(ids: ReadonlySet<number>, id: number): ReadonlySet<number> {
  const next = new Set(ids);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return next;
}
